import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router, UrlCreationOptions } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { ServiceModule } from '../../../service.module';
import { firstValueFrom } from 'rxjs';
import { ResourceDataService } from '../../rest/resource-data/resource-data.service';
import { Const, ResourceType } from '../../../../core/helpers/Const';
import { ResourceModel } from '../../../../core/models/resources/resource.model';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly resourceDataService: ResourceDataService
    ) {}

    public getNavigationEvents(route: ActivatedRoute) {
        return this.getRouter().events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => this.rootRoute(route)),
            filter((route: ActivatedRoute) => route.outlet === 'primary')
        );
    }

    private rootRoute(route: ActivatedRoute): ActivatedRoute {
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    public getRouter() {
        return this.router;
    }

    public navigateToProject(project: string, page: string): Promise<boolean> {
        return this.navigate(['project', project, page]);
    }

    public navigateToOrganization(project: string, organization: string): Promise<boolean> {
        return this.navigate(['project', project, 'organizations', organization]);
    }

    public goToResource(project: string, organization: string, resourceType:string, path: string, params = ""): Promise<boolean> {
        if(resourceType || path) {
            return this.navigate(['project', project, 'organizations', organization, resourceType], { queryParams: {path: path, params: params} });
        } else {
            return this.navigate(['project', project, 'organizations', organization]);
        }
    }

    public navigateToResource(resourceType: string, resourceName: string, route: ActivatedRoute): Promise<boolean> {
        return this.navigate([resourceType, resourceName], { queryParamsHandling: 'preserve', relativeTo: route });
    }

    public navigateTo404(): Promise<boolean> {
        return this.navigate(['404']);
    }

    public getNavigationUrl(params: string[], navigationExtras?: UrlCreationOptions) {
        return this.getRouter().createUrlTree(params, navigationExtras).toString();
    }

    public navigateToLoginPage(extras?: NavigationExtras) {
        return this.navigate(['login'], extras);
    }

    public navigateToPage(page: string, extras?: NavigationExtras) {
        return this.navigate([page], extras);
    }

    public navigateToEventsWithRelatedResource(relatedResource: string) {
        return this.navigate(["/events"], {
            queryParams: {
                relatedResource: relatedResource
            }
        });
    }

    public navigateToLogin(redirectUrl: string, idp: string, oidcPath?: string) {
        const url = new URL(window.location.origin + '/login');
        url.searchParams.set('redirecturl', redirectUrl);
        url.searchParams.set('idp', idp);
        if (idp === 'custom.idp' && oidcPath) {
            url.searchParams.set('oidcPath', oidcPath);
        }
        window.location.href = url.toString();
    }

    public navigateToLogout(idpLogout: string) {
        window.location.href = idpLogout;
    }

    public navigateBack() {
        this.location.back();
    }

    public openInNewTab(url: string) {
        window.open(url, '_blank');
    }

    private navigate(params: string[], extras?: NavigationExtras) {
        return this.getRouter().navigate(params, extras);
    }

    /**
     * Navigate to a resource by resolving its path segments and determining the correct navigation route.
     * Handles both container resources (folders, resource groups) and non-container resources.
     *
     * @param cleanedPathSegments - Array of path segments (without organization name)
     * @param organizationName - Name of the organization
     * @param resourceName - Name of the target resource
     * @param onNavigationComplete - Optional callback invoked when navigation completes successfully
     * @throws Error if the resource doesn't exist or user lacks permissions
     */
    async navigateToResourceByPath(
        cleanedPathSegments: string[],
        organizationName: string,
        resourceName: string,
        onNavigationComplete?: () => void
    ): Promise<void> {
        const resourcePathWithoutOrg = `${cleanedPathSegments.join('/')}/${resourceName}`;
        const parentPath = cleanedPathSegments.length > 0
            ? `${organizationName}/${cleanedPathSegments.join('/')}`.replace(/\/+$/, '')
            : organizationName;
        const resources = await firstValueFrom(this.resourceDataService.getResourcesInPath(parentPath, "validateParent=false"));
        const resourceModel = this._getResourceFromListByName(resources, resourceName);
        const containerTypes = [Const.FOLDERS, Const.RESOURCE_GROUPS];

        if (resourceModel) {
            const resourceTypeForUrl = this._getResourceTypeForUrl(resourceModel.type);
            if (containerTypes.includes(resourceTypeForUrl)) {
                await this.goToResource(
                    Const.ALL_PROJECTS_PARAM,
                    organizationName,
                    resourceTypeForUrl,
                    resourcePathWithoutOrg.startsWith(Const.ROOT_PATH) ? resourcePathWithoutOrg :
                        `${Const.ROOT_PATH}${resourcePathWithoutOrg}`
                );
                if (onNavigationComplete) {
                    onNavigationComplete();
                }
            } else {
                // If resource is not a container, navigate to resource parent and throw error
                throw new Error("Resource is not a container. Parent path: " + parentPath);
            }
        } else {
            throw new Error("Resource does not exist, or you don't have permissions to access it.");
        }
    }

    private _getResourceFromListByName(resources: ResourceModel[], resourceName: string): ResourceModel {
        return resources.find(resourceModel => resourceModel.name === resourceName);
    }

    private _getResourceTypeForUrl(resourceType: string): string {
        switch (resourceType) {
        case ResourceType.FOLDER:
            return Const.FOLDERS;
        case ResourceType.RESOURCE_GROUP:
            return Const.RESOURCE_GROUPS;
        default:
            return "";
        }
    }
}
